import { Award, Users, Target, Zap } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const About = () => {
  return (
    <div className="pt-32 pb-16 px-6 bg-white">
      <Helmet>
        <title>About ProjectProse | UK Property Management Company</title>
        <meta
          name="description"
          content="Learn how ProjectProse combines ARLA Propertymark-accredited property management with a premium short-term rental portfolio, backed by guaranteed rent, transparent pricing and dedicated account managers."
        />
        <link rel="canonical" href="https://www.project-prose.com/about" />
      </Helmet>
      <div className="max-w-7xl mx-auto mb-24">
        <div className="grid md:grid-cols-2 gap-20 items-start">
          <div className="space-y-8">
            <h2 className="text-sm uppercase tracking-[0.3em] text-prose-brand font-bold mb-4 border-b-4 w-fit border-prose-gold pb-2" data-aos="fade-in">About Us</h2>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-display font-bold text-prose-black leading-tight" data-aos="fade-up">
              Transforming properties into <span className="text-prose-brand">thriving</span> investments.
            </h2>
            <p className="text-gray-600 text-sm sm:text-base md:text-lg leading-relaxed text-justify" data-aos="fade-up" data-aos-delay="100">
              At ProjectProse, we operate as both a professional property management company and a property rental provider. We specialise in transforming properties into thriving investments for landlord clients while offering carefully curated rental properties for quality tenants. Our approach ensures excellence across both sectors through stress-free, high-quality services.
            </p>
            <p className="text-gray-600 text-sm sm:text-base md:text-lg leading-relaxed text-justify " data-aos="fade-up" data-aos-delay="200">
              As both landlord advocates and property providers, we understand the full spectrum of the rental market. For landlords, we manage the complexities of ownership - tenant sourcing, maintenance, compliance, and consistent income. For tenants, we provide quality rental homes backed by professional management and responsive support.
            </p>
            <div className="grid grid-cols-2 gap-8 pt-8 border-t border-gray-100" data-aos="fade-up" data-aos-delay="100">
              <div>
                <h4 className="font-bold text-prose-brand mb-2 text-sm sm:text-base" >Our Mission</h4>
                <p className="text-xs sm:text-sm text-gray-600 text-justify">To deliver exceptional property management services that guarantee consistent rental income, preserve the condition of your property, and provide peace of mind for our clients.</p>
              </div>
              <div>
                <h4 className="font-bold text-prose-brand mb-2 text-sm sm:text-base">Our Vision</h4>
                <p className="text-xs sm:text-sm text-gray-600 text-justify">To redefine property management by combining professionalism with a personal touch, ensuring landlords feel supported and tenants feel valued.</p>
              </div>
            </div>
          </div>
          
          <div 
            className="p-12 aspect-square flex flex-col justify-center rounded-xl overflow-hidden relative"
            style={{
              backgroundImage: 'url("https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=800&h=800&fit=crop&q=75")',
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}
          >
            <div className="absolute inset-0 bg-black/60 rounded-xl"></div>
            
            <div className="relative z-10">
              <h3 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-display text-white mb-4">Values</h3>
              <ul className="space-y-6">
                {['Trust', 'Excellence', 'Innovation'].map((val) => (
                  <li key={val} className="flex items-center gap-4 group">
                    <span className="h-[1px] w-8 bg-prose-gold group-hover:w-16 transition-all"></span>
                    <span className="uppercase tracking-[0.3em] font-semibold text-white text-xs sm:text-sm md:text-base">{val}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto mb-24 border-y border-gray-100 py-16" data-aos="fade-up">
        <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-display font-bold mb-12">Our Approach</h2>
        <div className="grid md:grid-cols-2 gap-12">
          <div className="space-y-6 text-gray-600 leading-relaxed text-justify text-sm sm:text-base">
            <p>
              Owning and managing property can be challenging, with endless tasks such as tenant sourcing, maintenance, compliance, and ensuring consistent rental income. At ProjectProse, we take the hassle out of the process, offering comprehensive solutions tailored to your needs.
            </p>
            <p>
              Our mission is simple: to provide stress-free, high-quality property management services that maximise your income and minimise your worries.
            </p>
          </div>
          <div className="space-y-6 text-gray-600 leading-relaxed text-justify text-sm sm:text-base">
            <p>
              Are you tired of dealing with the constant demands of property management? Imagine consistent, guaranteed rent every month, no matter the circumstances, and a trusted partner ensuring your property is cared for like it's their own.
            </p>
            <p>
              With ProjectProse expertise, your property is not just managed - it thrives. Contact us today to take the first step toward worry-free property management.
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto mb-24" data-aos="fade-up">
        <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-display font-bold mb-16 text-center">Why Choose ProjectProse?</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {[
            { icon: <Award size={32} />, title: "Certified Professionals", desc: "ARLA Propertymark accredited agents with proven expertise" },
            { icon: <Users size={32} />, title: "Dedicated Support", desc: "Personal account managers for every portfolio" },
            { icon: <Target size={32} />, title: "Transparent Pricing", desc: "No hidden fees, clear breakdown of all costs" },
            { icon: <Zap size={32} />, title: "Technology First", desc: "Smart systems for efficient portfolio management" }
          ].map((feature, i) => (
            <div key={i} className={`p-8 border rounded-xl transition-all duration-300 hover:scale-105 hover:-translate-y-2 text-justify ${
              i % 2 === 1
                ? 'border-prose-gold/30 hover:border-prose-gold hover:bg-prose-gold-light/10 hover:shadow-[0_0_20px_rgba(212,175,55,0.25)]'
                : 'border-gray-100 hover:border-prose-brand/30 hover:shadow-xl hover:bg-white'
            }`}>
              <div className={`mb-4 group-hover:scale-125 group-hover:rotate-12 transition-all duration-300 ${i % 2 === 1 ? 'text-prose-gold' : 'text-prose-brand'}`}>{feature.icon}</div>
              <h3 className="font-bold text-base sm:text-lg mb-2">{feature.title}</h3>
              <p className="text-gray-600 text-xs sm:text-sm">{feature.desc}</p>
            </div>
          ))}
        </div>
      </div>

    </div>
  );
};

export default About;